/**
 * PatientContextModal — реиспользуемый попап с клиническим контекстом пациента.
 * Данные получаются из Clinical Context Store (CCS) через бекенд-прокси.
 *
 * Дата: 04.05.2026
 * Обновление: 04.05.2026 — graceful fallback для ccs_unavailable / no_context.
 */
import { Brain, Calendar, ClipboardList, AlertTriangle, RefreshCw, User, WifiOff, FileQuestion } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { usePatientContext } from '@/hooks/usePatientContext';
import { cn } from '@/utils/cn';

interface PatientContextModalProps {
  phone: string;
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
  patientName?: string;
}

function SectionTitle({ icon: Icon, title }: { icon: React.ElementType; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4 text-indigo-500" />
      <span className="text-sm font-semibold text-slate-700">{title}</span>
    </div>
  );
}

function SignalBadge({ severity, value }: { severity?: string; value: string }) {
  const color =
    severity === 'high'
      ? 'bg-red-100 text-red-700 border-red-200'
      : severity === 'medium'
        ? 'bg-amber-100 text-amber-700 border-amber-200'
        : 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={cn('text-xs px-2 py-0.5 rounded-full border', color)}>{value}</span>
  );
}

/** Нейтральная информационная плашка (не жёлтый баннер ошибки). */
function InfoBox({
  icon: Icon,
  children,
  variant = 'neutral',
}: {
  icon: React.ElementType;
  children: React.ReactNode;
  variant?: 'neutral' | 'warning';
}) {
  const style =
    variant === 'warning'
      ? 'bg-amber-50 border-amber-200 text-amber-700'
      : 'bg-slate-50 border-slate-200 text-slate-500';
  return (
    <div className={cn('flex items-center gap-2 p-3 rounded-lg border text-sm', style)}>
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span>{children}</span>
    </div>
  );
}

export function PatientContextModal({
  phone,
  tenantId,
  isOpen,
  onClose,
  patientName,
}: PatientContextModalProps) {
  const { data, isLoading, isError, refetch, isFetching } = usePatientContext(
    isOpen ? phone : null,
    tenantId
  );

  // При успешном ответе имя берём из data.data (новый формат)
  const name = data?.data?._patient_name || patientName || phone;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size="lg"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Brain className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Контекст пациента</h2>
            <p className="text-sm text-slate-500">{name}</p>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
          title="Обновить"
        >
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')} />
        </button>
      </div>

      {/* Загрузка */}
      {isLoading && (
        <div className="flex items-center justify-center py-10 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin mr-2" />
          <span className="text-sm">Загрузка контекста...</span>
        </div>
      )}

      {/* Сетевая ошибка (axios/fetch) — не ответ от бэкенда */}
      {isError && !isLoading && (
        <InfoBox icon={AlertTriangle} variant="warning">
          Не удалось связаться с сервером. Проверьте подключение.
        </InfoBox>
      )}

      {/* Успешный ответ от бэкенда — разбираем по status */}
      {data && !isLoading && (
        <>
          {/* CCS недоступен */}
          {data.status === 'ccs_unavailable' && (
            <InfoBox icon={WifiOff}>
              {data.message || 'CCS временно недоступен'}
            </InfoBox>
          )}

          {/* Нет данных в CCS */}
          {data.status === 'no_context' && (
            <InfoBox icon={FileQuestion}>
              {data.message || 'Данные по пациенту в CCS ещё не загружены'}
            </InfoBox>
          )}

          {/* Успешные данные */}
          {data.status === 'ok' && data.data && (
            <div className="space-y-5">
              {/* Patient ID */}
              {data.data._patient_id && data.data._patient_id !== phone && (
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <User className="w-3.5 h-3.5" />
                  <span>ID: {data.data._patient_id}</span>
                </div>
              )}

              {/* Signals */}
              {Array.isArray(data.data.signals) && data.data.signals.length > 0 && (
                <div>
                  <SectionTitle icon={AlertTriangle} title="Сигналы" />
                  <div className="flex flex-wrap gap-1.5">
                    {data.data.signals.map((s, i) => (
                      <SignalBadge key={i} severity={s.severity} value={s.name + (s.value ? ': ' + s.value : '')} />
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              {data.data.summary && (
                <div>
                  <SectionTitle icon={Brain} title="Сводка" />
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{data.data.summary}</p>
                </div>
              )}

              {/* Appointments */}
              {Array.isArray(data.data.appointments) && data.data.appointments.length > 0 && (
                <div>
                  <SectionTitle icon={Calendar} title="Предстоящие записи" />
                  <div className="space-y-1.5">
                    {data.data.appointments.map((appt, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm p-2 bg-slate-50 rounded-lg border border-slate-100">
                        <Calendar className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium text-slate-700">{appt.date}</span>
                          <span className="text-slate-500 mx-1">·</span>
                          <span className="text-slate-600">{appt.service}</span>
                          {appt.doctor && <span className="text-slate-400 ml-1">({appt.doctor})</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {Array.isArray(data.data.recommendations) && data.data.recommendations.length > 0 && (
                <div>
                  <SectionTitle icon={ClipboardList} title="Рекомендации" />
                  <ul className="space-y-1">
                    {data.data.recommendations.map((rec, i) => (
                      <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                        <span className="text-indigo-400 font-bold mt-0.5">·</span>
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Treatment plan */}
              {data.data.treatment_plan && (
                <div>
                  <SectionTitle icon={ClipboardList} title="План лечения" />
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{data.data.treatment_plan}</p>
                </div>
              )}

              {/* Fallback: сырые данные */}
              {!data.data.signals && !data.data.summary && !data.data.appointments && !data.data.treatment_plan && (
                <div className="text-xs text-slate-400 p-3 bg-slate-50 rounded-lg font-mono overflow-x-auto">
                  <pre>{JSON.stringify(data.data, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
